import React from "react";

const STATUS_BADGE_CLASS = {
  delivered: "bg-green-500/20 text-green-400",
  shipped: "bg-blue-500/20 text-blue-400",
  cancelled: "bg-red-500/20 text-red-400",
  confirmed: "bg-emerald-500/20 text-emerald-400",
};
const STATUS_LABEL = {
  pending: "In attesa",
  confirmed: "Confermato",
  shipped: "Spedito",
  delivered: "Consegnato",
  cancelled: "Annullato",
};
const PAYMENT_METHOD_LABEL = {
  cash: "Contanti alla consegna",
  paypal: "PayPal",
  stripe: "Carta (Stripe)",
  transfer: "Bonifico bancario",
  satispay: "Satispay",
  klarna: "Klarna",
};

const statusBadgeClass = (status) => STATUS_BADGE_CLASS[status] || "bg-amber-500/20 text-amber-400";
const statusLabel = (status) => STATUS_LABEL[status] || status;
const paymentMethodLabel = (method) => PAYMENT_METHOD_LABEL[method] || method;

export const OrderDetailModal = ({ order, onClose }) => {
  if (!order) return null;
  const formatDate = (d) => d ? new Date(d).toLocaleString('it-IT', { day:'2-digit', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '';
  const subtotal = (order.items || []).reduce((s, i) => s + ((i.price_unit || i.price || 0) * (1 + (parseFloat(i.iva_percentage) || 0) / 100)) * (i.quantity || 1), 0);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="order-detail-modal">
      <div className="bg-[#12121a] border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#12121a] border-b border-gray-800 p-5 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-white">Ordine #{(order.id || '').slice(0,8).toUpperCase()}</h3>
            <p className="text-gray-500 text-xs mt-1">{formatDate(order.order_date)}</p>
          </div>
          <button onClick={onClose} data-testid="order-detail-close" className="text-gray-500 hover:text-white text-2xl">×</button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusBadgeClass(order.status)}`}>
              {statusLabel(order.status)}
            </span>
            {order.payment_status === 'paid' && (
              <span className="text-xs px-3 py-1 rounded-full bg-green-500/20 text-green-400 font-medium">Pagato</span>
            )}
            {order.tracking_number && (
              <span className="text-xs px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 font-medium">Tracking: {order.tracking_number}</span>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">Articoli ({(order.items || []).length})</h4>
            <div className="space-y-2">
              {(order.items || []).map((item, idx) => (
                <div key={item.article_id || `${order.id}-item-${idx}`} className="bg-[#0a0a0f] border border-gray-800 rounded-lg p-3 flex justify-between items-center">
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">{item.title}</div>
                    <div className="text-gray-500 text-xs mt-0.5">Quantità: {item.quantity || 1}</div>
                  </div>
                  <div className="text-amber-400 font-bold text-sm">
                    {(((item.price_unit || item.price || 0) * (1 + (parseFloat(item.iva_percentage) || 0) / 100)) * (item.quantity || 1)).toFixed(2)} €
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-800 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-400">
              <span>Subtotale</span>
              <span>{subtotal.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Spedizione</span>
              <span>{(order.shipping_cost || 0).toFixed(2)} €</span>
            </div>
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Sconto{order.coupon_code ? ` (${order.coupon_code})` : ''}</span>
                <span>−{(order.discount_amount || 0).toFixed(2)} €</span>
              </div>
            )}
            <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-gray-800">
              <span>Totale</span>
              <span className="text-amber-400">{(order.total_amount || 0).toFixed(2)} €</span>
            </div>
          </div>

          {order.customer_info && (
            <div className="border-t border-gray-800 pt-4">
              <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">Indirizzo di spedizione</h4>
              <div className="bg-[#0a0a0f] border border-gray-800 rounded-lg p-3 text-sm text-gray-300 leading-relaxed">
                <div className="font-medium text-white">{order.customer_info.name}</div>
                <div>{order.customer_info.address}</div>
                <div>{order.customer_info.zipCode} {order.customer_info.city}</div>
                <div>{order.customer_info.specificCountry || order.customer_info.country}</div>
                <div className="text-gray-500 mt-2 pt-2 border-t border-gray-800">
                  {order.customer_info.email}<br />
                  {order.customer_info.phone}
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-800 pt-4 text-sm text-gray-400">
            <span className="font-medium text-gray-500">Metodo di pagamento:</span>{' '}
            <span className="text-white">
              {paymentMethodLabel(order.payment_method)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
